import React, { useCallback, useState } from 'react';

import { Box, Pressable, Text } from '@rbilabs/universal-components';
import { noop } from 'lodash-es';
import { useIntl } from 'react-intl';

import CreditCardFormInputs, { getErrorMessage } from 'components/credit-card-form-inputs';
import { LayoutContainer } from 'components/layout/styled';
import { SafetechEncryption } from 'components/payments/integrations/orbital/components/encryption';
import {
  IEncryptionError,
  IEncryptionResult,
} from 'components/payments/integrations/orbital/components/encryption/types';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { useValidateCCNumberInClientExperiment } from 'experiments/use-validate-cc-number-in-client';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRoute } from 'hooks/navigation/use-route';
import useEffectOnce from 'hooks/use-effect-once';
import useErrorModal from 'hooks/use-error-modal';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { PaymentMethodsRouteParams } from 'pages/account/types';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  SupportedCardType,
  defaultPaymentFieldVariation,
  defaultSupportedCardTypes,
} from 'state/launchdarkly/variations';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { portalToFooter } from 'state/mobile-footer';
import { portalToReplaceHeader } from 'state/mobile-header-nav';
import { useOrderContext } from 'state/order';
import { usePaymentContext } from 'state/payment';
import { CardType } from 'state/payment/types';
import { primitive } from 'styles/constants/primitives';
import logger from 'utils/logger';
import {
  getCCFormErrors,
  initialErrorState,
  initialPaymentState,
  mapDeliveryToCCFormAddress,
  onCCFormChange,
  testCardPaymentState,
} from 'utils/payment';
import { mapPaymentMethodCardTypes } from 'utils/payment/map-payment-method-card-type';
import { routes } from 'utils/routing';

import { Header, ModalContent, StyledActionButton, StyledBackIcon } from './styled';

export const AddPaymentMethodModalRedesign: React.FC<React.PropsWithChildren<{
  onCardAdded: () => void;
}>> = ({ onCardAdded = noop }) => {
  const { user } = useAuthContext();
  const [errors, setErrors] = useState(initialErrorState);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { loading: isUserLoading } = useLoyaltyUserState();

  const {
    addPaymentMethod,
    transformPaymentValues,
    isOrbital,
    setEncryptionResult,
    setLoading: setIsPaymentLoading,
    isQuickPayMethodSelected,
    setIsQuickPayMethodSelected,
    setIsAddPaymentMethodSuccess,
    setCheckoutPaymentMethodId,
    setDefaultPaymentMethodId,
  } = usePaymentContext();
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;

  const rawSupportedCardTypes: SupportedCardType[] =
    useFlag(LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS) || defaultSupportedCardTypes;
  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];

  const { deliveryAddress, isDelivery: isDeliveryServiceMode } = useOrderContext();
  const { goBack } = useNavigation();
  const {
    pathname,
    params: { accountToDelete },
  } = useRoute<PaymentMethodsRouteParams>();
  const isDelivery = pathname.startsWith(routes.cart) && isDeliveryServiceMode;
  const isAccountPayments = !!pathname.startsWith(routes.payment);
  const { feCountryCode: billingCountry } = useLocale();
  const isMobile = useIsMobileBreakpoint();
  const userDetailsName = user?.details.name;

  const [paymentValues, setPaymentValues] = useState(
    initialPaymentState({
      isDelivery,
      deliveryAddress,
      billingCountry,
      userDetailsName,
    })
  );
  const { formatMessage } = useIntl();
  const autoFill = useFlag(LaunchDarklyFlag.AUTO_FILL_TEST_CARD);
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Credit Card details',
  });
  const [isEncryptionActive, setIsEncryptionActive] = useState(false);
  const [isDefaultPayment, setIsDefaultPayment] = useState(false);

  const validateCCExperiment = useValidateCCNumberInClientExperiment();

  useEffectOnce(() => {
    if (autoFill) {
      setPaymentValues(testCardPaymentState({ isDelivery, paymentFieldVariations }));
    }
  });

  const handleChange = useCallback(
    (name: any, value: any) => {
      const { state, formErrors } = onCCFormChange(
        name,
        value,
        paymentValues,
        errors,
        formatMessage,
        supportedCardTypes,
        validateCCExperiment
      );
      setErrors(prevState => ({ ...prevState, ...formErrors }));
      setPaymentValues(prevState => {
        return state.billingAddressSameAsDelivery
          ? {
              ...prevState,
              ...state,
              ...mapDeliveryToCCFormAddress(deliveryAddress),
            }
          : { ...prevState, ...state };
      });
    },
    [
      paymentValues,
      errors,
      formatMessage,
      deliveryAddress,
      supportedCardTypes,
      validateCCExperiment,
    ]
  );

  const addNewCard = useCallback(
    async (event: any, encryptionResult?: IEncryptionResult) => {
      if (event) {
        event.preventDefault();
      }

      const onAddCardSuccess = () => {
        onCardAdded();
        setPaymentValues(initialPaymentState({ isDelivery, deliveryAddress }));
        if (isQuickPayMethodSelected) {
          setIsAddPaymentMethodSuccess(true);
          setIsQuickPayMethodSelected(false);
        }

        if (isAccountPayments) {
          // Setting loading as true if we comes from account page
          setIsPaymentLoading(true);
        }
        goBack();
        setIsLoading(false);
      };

      const onAddCardFailure = (paymentMethodError: Error, message: string) => {
        logger.error({ error: paymentMethodError, message });
        openErrorDialog({
          message: formatMessage({ id: 'paymentAddingError' }),
          error: paymentMethodError,
        });
        setIsLoading(false);
      };

      setIsLoading(true);
      const reshapedPaymentValues = transformPaymentValues({
        paymentValues,
        paymentFieldVariations,
      });
      reshapedPaymentValues.accountToDelete = accountToDelete;

      try {
        const newPaymentValueId = await addPaymentMethod(
          reshapedPaymentValues,
          {
            skipErrorDialogOnError: true,
          },
          encryptionResult
        );
        if (isDefaultPayment) {
          setCheckoutPaymentMethodId(newPaymentValueId);
          setDefaultPaymentMethodId(newPaymentValueId);
        }
        onAddCardSuccess();
      } catch (error) {
        // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
        onAddCardFailure(error, 'Error adding payment method');
      }
    },
    [
      transformPaymentValues,
      paymentValues,
      paymentFieldVariations,
      accountToDelete,
      onCardAdded,
      isDelivery,
      deliveryAddress,
      isQuickPayMethodSelected,
      setIsQuickPayMethodSelected,
      isAccountPayments,
      goBack,
      setIsPaymentLoading,
      openErrorDialog,
      formatMessage,
      addPaymentMethod,
      setIsAddPaymentMethodSuccess,
    ]
  );

  const handleOnPress = useCallback(
    async (event: any) => {
      if (event) {
        event.preventDefault();
      }
      const { hasErrors, formErrors } = getCCFormErrors(
        paymentValues,
        errors,
        formatMessage,
        paymentFieldVariations,
        billingCountry
      );

      if (hasErrors) {
        // TODO: RN - Focus on first field with errors
        const newErrorMessage = getErrorMessage({ errors, formatMessage });
        setErrorMessage(newErrorMessage);

        return setErrors(prevState => ({ ...prevState, ...formErrors, didAttemptSubmit: true }));
      }

      if (isOrbital) {
        setEncryptionResult(undefined);
        setIsLoading(true);
        return setIsEncryptionActive(true);
      }
      addNewCard(null);
    },
    [
      addNewCard,
      billingCountry,
      errors,
      formatMessage,
      isOrbital,
      paymentFieldVariations,
      paymentValues,
      setEncryptionResult,
    ]
  );

  const onEncryptionResult = (data: IEncryptionResult) => {
    setEncryptionResult(data);
    setIsEncryptionActive(false);
    addNewCard(null, data);
  };

  // Handle encryption error
  const onEncryptionError = (error: IEncryptionError) => {
    setIsEncryptionActive(false);
    setEncryptionResult(undefined);
    setIsLoading(false);
    const errorMessage = error.error;
    logger.error({ error: new Error(errorMessage), message: errorMessage });
    openErrorDialog({
      message: formatMessage({ id: 'paymentAddingError' }),
      error: new Error(errorMessage),
    });
  };

  const HiddenAppHeader = portalToReplaceHeader(() => {
    return (
      <Box _ios={{ marginTop: '$12' }} _android={{ marginTop: '$8' }} marginTop="$0">
        <Pressable
          onPress={() => {
            goBack();
          }}
          padding="$4"
        >
          <StyledBackIcon variant="back" />
        </Pressable>
        <Header alignSelf="center" variant="headerTwo" top="$4" fontSize="lg" position="absolute">
          {`Add Debit / Credit Card`}
        </Header>
      </Box>
    );
  });

  const HiddenAppFooter = portalToFooter(() => {
    if (isLoading || isUserLoading) {
      return (
        <Box padding="$4" bgColor={primitive.$white}>
          <StyledActionButton isLoading={true}></StyledActionButton>
        </Box>
      );
    }

    return (
      <Box padding="$4" bgColor={primitive.$white}>
        <StyledActionButton onPress={handleOnPress}>
          {formatMessage({ id: 'addToCart' })}
        </StyledActionButton>
      </Box>
    );
  });

  return (
    <LayoutContainer isFullContainer={isMobile}>
      <ModalContent testID="add-payment-modal-heading" py="$0">
        {!!errorMessage && (
          <VisuallyHidden role="alert" accessibilityLabel="">
            <Text>{errorMessage}</Text>
          </VisuallyHidden>
        )}
        <CreditCardFormInputs
          onChange={handleChange}
          paymentValues={paymentValues}
          errors={errors}
          isDelivery={isDelivery}
          isDefaultPayment={isDefaultPayment}
          setIsDefaultPayment={setIsDefaultPayment}
        />
      </ModalContent>
      <HiddenAppHeader />
      <HiddenAppFooter />
      <ErrorDialog />
      {isOrbital && isEncryptionActive ? (
        <SafetechEncryption
          cardNumber={paymentValues.cardNumber}
          cvv={paymentValues.cvv || ''}
          onResult={onEncryptionResult}
          onError={onEncryptionError}
        />
      ) : null}
    </LayoutContainer>
  );
};
